import { motion } from 'framer-motion';
import { Star, Quote, Briefcase } from 'lucide-react';
import SEO from '../components/SEO';

const Testimonials = () => {
  const testimonials = [
    {
      name: 'Priya Sharma',
      course: 'Tally with GST',
      rating: 5,
      text: 'Best decision I ever made! The personal attention and daily tracking helped me understand concepts I struggled with for months at other institutes. Got a job within 2 months of completing the course.',
      jobTitle: 'Accountant at Local Firm',
    },
    {
      name: 'Rahul Kumar',
      course: 'C & C++ Programming',
      rating: 5,
      text: 'Unlike other institutes where you just a face in the crowd, here the instructor actually cares if you learn. The 1:1 accountability model is game-changing. Worth every rupee!',
      jobTitle: 'Junior Developer',
    },
    {
      name: 'Anjali Patil',
      course: 'MS Office Suite',
      rating: 5,
      text: 'I was struggling with Excel formulas and pivot tables. The instructor patiently taught me until I got it perfect. Now I handle all the data work at my office confidently.',
      jobTitle: 'Office Administrator',
    },
    {
      name: 'Suresh Reddy',
      course: 'Photoshop & CorelDraw',
      rating: 5,
      text: 'The practical approach is amazing. By the end of the course, I had created a complete portfolio. Started my own small design business thanks to Genius Academy!',
      jobTitle: 'Freelance Designer',
    },
    {
      name: 'Kavita Joshi',
      course: 'Computer Fundamentals',
      rating: 5,
      text: 'I had zero computer knowledge. Was very nervous. But the teaching style is so patient and clear. Now I can confidently use computers for work and personal tasks.',
      jobTitle: 'Bank Employee',
    },
    {
      name: 'Arun Desai',
      course: 'AI Tools Training',
      rating: 5,
      text: 'Learning ChatGPT and AI tools has transformed how I work. The instructor showed real-world applications. My productivity has doubled! Highly recommend this course.',
      jobTitle: 'Business Owner',
    },
    {
      name: 'Deepa Singh',
      course: 'Tally with GST',
      rating: 5,
      text: 'The difference between Genius Academy and others is accountability. They don\'t just teach and forget. They make sure you actually learn. Got placed immediately after course.',
      jobTitle: 'Accounts Executive',
    },
    {
      name: 'Vikram Rao',
      course: 'MS Excel Advanced',
      rating: 5,
      text: 'Excel was always intimidating. The step-by-step teaching and practical examples made it so easy to understand. Now I train others in my office!',
      jobTitle: 'Data Analyst',
    },
    {
      name: 'Meera Kulkarni',
      course: 'PageMaker & DTP',
      rating: 5,
      text: 'Started the course with zero design knowledge. The personal attention and project-based learning gave me the confidence to pursue a career in design.',
      jobTitle: 'DTP Operator',
    },
  ];

  return (
    <div className="pt-16 md:pt-20">
      <SEO
        title="Student Reviews & Success Stories — Genius Academy Kalaburagi"
        description="Read real success stories and reviews from Genius Academy students who completed MS Office, Tally, Python, Web Development, Photoshop, CorelDRAW, and other computer courses in Kalaburagi (Gulbarga)."
        path="/testimonials"
        keywords="Genius Academy reviews, student testimonials, computer institute Kalaburagi reviews, computer classes Gulbarga feedback"
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Testimonials', path: '/testimonials' }]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-cyan-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-6xl font-bold tracking-tight text-slate-900 mb-6" data-testid="testimonials-page-title">
              Success <span className="text-cyan-600">Stories</span>
            </h1>
            <p className="text-base md:text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Don't just take our word for it. Here's what our students say about their learning experience 
              at Genius Academy.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md transition-all duration-300 p-6 relative"
                data-testid={`testimonial-card-${index}`}
              >
                <Quote className="absolute top-4 right-4 w-8 h-8 text-cyan-100" />
                
                {/* Rating */}
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                {/* Testimonial Text */}
                <p className="text-slate-700 mb-6 leading-relaxed italic">
                  "{testimonial.text}"
                </p>

                {/* Student Info */}
                <div className="border-t border-slate-100 pt-4">
                  <p className="font-bold text-slate-900 text-lg">{testimonial.name}</p>
                  <p className="text-sm text-cyan-600 font-medium">{testimonial.course}</p>
                  <p className="text-sm text-slate-500 mt-1">{testimonial.jobTitle}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-cyan-600 mb-2">500+</div>
              <p className="text-slate-600 font-medium">Students Trained</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-center"
              data-testid="stat-placement-assistance"
            >
              <div className="w-16 h-16 md:w-[72px] md:h-[72px] mx-auto mb-3 rounded-2xl bg-cyan-100 flex items-center justify-center">
                <Briefcase className="w-8 h-8 md:w-9 md:h-9 text-cyan-600" strokeWidth={2.25} />
              </div>
              <p className="text-lg md:text-xl font-bold text-slate-900">Placement Assistance</p>
              <p className="text-slate-600 text-sm mt-1">Career Guidance &amp; Interview Prep</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-cyan-600 mb-2">15+</div>
              <p className="text-slate-600 font-medium">Courses Offered</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="text-center"
            >
              <div className="text-4xl md:text-5xl font-bold text-cyan-600 mb-2">100%</div>
              <p className="text-slate-600 font-medium">Practical Training</p>
            </motion.div>
          </div>

          {/* Placement Assistance details + disclaimer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mt-14 md:mt-16 rounded-2xl bg-white border border-slate-100 shadow-sm p-6 md:p-10"
            data-testid="placement-assistance-block"
          >
            <div className="grid md:grid-cols-4 gap-4 md:gap-6 text-center md:text-left">
              <div className="md:col-span-1">
                <p className="text-xs font-semibold uppercase tracking-wider text-cyan-600 mb-2">Beyond the Classroom</p>
                <h3 className="text-xl md:text-2xl font-bold text-slate-900 leading-tight">
                  Placement Assistance &amp; Career Support
                </h3>
              </div>
              <ul className="md:col-span-3 grid sm:grid-cols-2 gap-3 text-sm md:text-base">
                <li className="flex items-start gap-2 text-slate-700">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Career Guidance &amp; Placement Assistance
                </li>
                <li className="flex items-start gap-2 text-slate-700">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Resume Building &amp; Interview Preparation
                </li>
                <li className="flex items-start gap-2 text-slate-700">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Industry-Oriented Training
                </li>
                <li className="flex items-start gap-2 text-slate-700">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-cyan-500 flex-shrink-0" />
                  Mock Interviews &amp; Communication Skills
                </li>
              </ul>
            </div>
            <p
              className="mt-6 md:mt-8 text-xs md:text-sm text-slate-500 leading-relaxed border-t border-slate-100 pt-4"
              data-testid="placement-disclaimer"
            >
              <span className="font-semibold text-slate-600">Disclaimer:</span> Placement assistance is provided to eligible
              students. Final job selection depends on the student's skills, interview performance, employer requirements,
              and available opportunities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-cyan-600 to-blue-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-bold mb-6">
              Your Success Story Starts Here
            </h2>
            <p className="text-lg md:text-xl mb-8 opacity-90">
              Join hundreds of students who transformed their careers with Genius Academy.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white text-cyan-600 hover:bg-slate-50 rounded-full px-8 py-4 font-bold shadow-lg transition-all"
                data-testid="testimonials-cta-contact"
              >
                Start Your Journey
              </a>
              <a
                href="tel:9164998997"
                className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-4 font-bold transition-all"
                data-testid="testimonials-cta-call"
              >
                Talk to Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;